"use client"

import { useState, useEffect } from "react"
import { Plus, X, GripVertical, Save } from "lucide-react"
import { getSchool, updateEducationSystem } from "@/hooks/api/schoolApi"
import config from "../../../../../config"

const COUNTRIES = ["Nigeria", "Ghana", "Kenya", "South Africa", "Uganda", "Tanzania", "United Kingdom", "United States", "Canada", "Other"]

const EXAM_BODIES: Record<string, string[]> = {
  Nigeria: ["WAEC", "NECO", "NABTEB", "NBAIS"],
  Ghana: ["WAEC (Ghana)", "BECE"],
  Kenya: ["KNEC", "KCSE", "KCPE"],
  "South Africa": ["DBE", "IEB", "SACAI"],
  "United Kingdom": ["AQA", "Edexcel", "OCR", "Cambridge"],
  "United States": ["College Board (SAT/AP)", "ACT"],
  Other: ["Other"],
}

const DEFAULT_CLASS_LEVELS: Record<string, string[]> = {
  WAEC: ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"],
  NECO: ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"],
  NABTEB: ["ND1", "ND2", "HND1", "HND2"],
  "WAEC (Ghana)": ["JHS1", "JHS2", "JHS3", "SHS1", "SHS2", "SHS3"],
  KNEC: ["Grade 7", "Grade 8", "Grade 9", "Form 1", "Form 2", "Form 3", "Form 4"],
  KCSE: ["Form 1", "Form 2", "Form 3", "Form 4"],
  AQA: ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"],
  Edexcel: ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"],
  Other: ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6"],
}

export function EducationSystemTab() {
  const [country, setCountry] = useState("")
  const [examBody, setExamBody] = useState("")
  const [classLevels, setClassLevels] = useState<string[]>([])
  const [newLevel, setNewLevel] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (config.appMode === "prototype") {
      setCountry("Nigeria")
      setExamBody("WAEC")
      setClassLevels(["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"])
      setLoading(false)
      return
    }
    getSchool()
      .then((s) => {
        const es = (s as any).educationSystem ?? {}
        setCountry(es.country ?? "Nigeria")
        setExamBody(es.examBody ?? "WAEC")
        setClassLevels(es.classLevels ?? DEFAULT_CLASS_LEVELS["WAEC"] ?? [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleCountryChange = (c: string) => {
    setCountry(c)
    const bodies = EXAM_BODIES[c] ?? []
    const firstBody = bodies[0] ?? ""
    setExamBody(firstBody)
    setClassLevels(DEFAULT_CLASS_LEVELS[firstBody] ?? [])
  }

  const handleExamBodyChange = (eb: string) => {
    setExamBody(eb)
    setClassLevels(DEFAULT_CLASS_LEVELS[eb] ?? [])
  }

  const addLevel = () => {
    const trimmed = newLevel.trim()
    if (trimmed && !classLevels.includes(trimmed)) {
      setClassLevels((prev) => [...prev, trimmed])
      setNewLevel("")
    }
  }

  const removeLevel = (level: string) => {
    setClassLevels((prev) => prev.filter((l) => l !== level))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (config.appMode !== "prototype") {
        await updateEducationSystem({ country, examBody, classLevels })
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-400 py-8 text-center">Loading education settings…</div>
  }

  const examBodies = EXAM_BODIES[country] ?? ["Other"]

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-slate-900">Education System</h3>
        <p className="text-sm text-slate-500 mt-0.5">Configure the curriculum system and class levels used at your school.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Country</label>
          <select
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Exam Body / Curriculum</label>
          <select
            value={examBody}
            onChange={(e) => handleExamBodyChange(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            {examBodies.map((eb) => (
              <option key={eb} value={eb}>{eb}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Class Levels</label>
        <p className="text-xs text-slate-400 mt-0.5 mb-3">These are the class levels students can enrol in at your school.</p>

        <div className="flex flex-wrap gap-2 mb-3">
          {classLevels.map((level) => (
            <div key={level} className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              <GripVertical size={10} className="text-indigo-300" />
              {level}
              <button onClick={() => removeLevel(level)} className="ml-0.5 hover:text-red-500">
                <X size={10} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            value={newLevel}
            onChange={(e) => setNewLevel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLevel()}
            placeholder="Add custom level (e.g. Pre-SS1)"
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <button
            onClick={addLevel}
            disabled={!newLevel.trim()}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-xl text-sm font-semibold text-slate-700"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl"
        >
          <Save size={14} />
          {saving ? "Saving…" : saved ? "Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  )
}
